/**
 * @description : Cancellation workflow for flights. Ops continuation of a
 *                customer-raised cancel request (always initiated in MyTrip; ops
 *                never starts one). Drives the cancel row via GET /status and the
 *                ops-facing endpoints documented in Cancellation-flow
 *                (SMB-2905/2906/2907/2908), engine paths under /be/v1/flight/cancel/*.
 *
 *                Sits inside TransactionList as a full-page takeover (no route).
 *                Mode/sector SELECTION happens at /request (customer/MyTrip side,
 *                mirrors the modify flow); here we display the chosen mode and the
 *                cancelled sectors read-only and run the quote -> execute -> refund
 *                lifecycle.
 * @author: Azamuddin
 * @date: 05/27/2026
 */

//********IMPORTS STARTS*********/
import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import BlockIcon from "@mui/icons-material/Block";
import {
    CancelPaxRate,
    CancelPhase,
    CancelSector,
    CancelStatus,
    CancelStatusResponse,
    ManualQuoteInputRate,
    PaxType,
    PtrType,
    executeCancel,
    generateQuote,
    getCancelStatus,
    isTerminalCancelStatus,
    markCancelFailed,
    markRefunded,
    normalizePtrType,
    sendQuote,
    submitManualQuote,
} from "../operations/cancellationService";
// @ts-ignore -- authSession is JS
import { getUserDetails } from "../operations/authSession";
//********IMPORTS ENDS*********/

//********CONSTANTS STARTS*********/
const STEPS = ["Requested", "Quote", "Customer", "Execute", "Refund"];

const STATE_TO_STEP: Record<CancelStatus, number> = {
    CANCEL_REQUESTED: 0,
    QUOTE_PENDING: 1,
    QUOTE_READY: 1,
    SENT_TO_CUSTOMER: 2,
    CUSTOMER_APPROVED: 3,
    CANCELLING: 3,
    SUPPLIER_CONFIRMED: 3,
    REFUND_PENDING: 4,
    CANCELLED: 4,
    CANCEL_FAILED: 3,
    QUOTE_EXPIRED: 2,
    AUTO_CANCELLED: 1,
    FAILED: 1,
};

const STATE_LABEL: Record<CancelStatus, string> = {
    CANCEL_REQUESTED: "Cancel requested",
    QUOTE_PENDING: "Quote pending",
    QUOTE_READY: "Quote ready",
    SENT_TO_CUSTOMER: "Sent to customer",
    CUSTOMER_APPROVED: "Customer approved",
    CANCELLING: "Cancelling",
    SUPPLIER_CONFIRMED: "Supplier confirmed",
    REFUND_PENDING: "Refund pending",
    CANCELLED: "Cancelled",
    CANCEL_FAILED: "Cancel failed",
    QUOTE_EXPIRED: "Quote expired",
    AUTO_CANCELLED: "Auto-cancelled",
    FAILED: "Failed",
};

const STATE_CHIP_COLOR: Record<CancelStatus, { bg: string; text: string }> = {
    CANCEL_REQUESTED: { bg: "#FEF2F2", text: "#991B1B" },
    QUOTE_PENDING: { bg: "#FFFBEB", text: "#92400E" },
    QUOTE_READY: { bg: "#F5F3FF", text: "#7C3AED" },
    SENT_TO_CUSTOMER: { bg: "#ECFDF5", text: "#065F46" },
    CUSTOMER_APPROVED: { bg: "#ECFDF5", text: "#065F46" },
    CANCELLING: { bg: "#FFFBEB", text: "#92400E" },
    SUPPLIER_CONFIRMED: { bg: "#EFF6FF", text: "#1E40AF" },
    REFUND_PENDING: { bg: "#FFFBEB", text: "#92400E" },
    CANCELLED: { bg: "#ECFDF5", text: "#065F46" },
    CANCEL_FAILED: { bg: "#FEF2F2", text: "#991B1B" },
    QUOTE_EXPIRED: { bg: "#F4F5F9", text: "#6B7280" },
    AUTO_CANCELLED: { bg: "#F4F5F9", text: "#6B7280" },
    FAILED: { bg: "#FEF2F2", text: "#991B1B" },
};

const PHASE_LABEL: Record<CancelPhase, string> = {
    AUTO: "Auto - polling supplier",
    OPS: "Ops handoff - 24h",
    GRACE: "Final grace - 4h",
};

// Opt-in auto-refresh cadence per status (seconds). We never poll silently.
const POLL_CADENCE_SECONDS: Partial<Record<CancelStatus, number>> = {
    QUOTE_PENDING: 30,
    CANCELLING: 30,
    SUPPLIER_CONFIRMED: 5,
};

const PROMPT_DELAY_MS = 10_000;

const COLORS = {
    ind: "#4F46E5",
    indBg: "#EEF2FF",
    border: "#E2E5EF",
    bg: "#F7F8FC",
    card: "#FFFFFF",
    text: "#141824",
    dim: "#6B7280",
    lgray: "#F4F5F9",
    grn: "#059669",
    grnDk: "#065F46",
    grnBg: "#ECFDF5",
    amb: "#D97706",
    ambDk: "#92400E",
    ambBg: "#FFFBEB",
    red: "#DC2626",
    redDk: "#991B1B",
    redBg: "#FEF2F2",
    blu: "#2563EB",
    bluDk: "#1E40AF",
    bluBg: "#EFF6FF",
};
//********CONSTANTS ENDS*********/

//********INTERFACES STARTS*********/
interface CancelBookingWorkflowProps {
    // The customer-raised cancel request to process (from the transactions list).
    // Ops never initiates a cancel; it only continues an existing cancel row.
    cancelTransactionId: string;
    itineraryId: string;
    customerName?: string;
    customerEmail?: string;
    onBack: () => void;
}

interface ToastState {
    open: boolean;
    severity: "error" | "success" | "info" | "warning";
    message: string;
    errorReference?: string;
}

interface FetchError {
    message: string;
    errorReference?: string;
}

interface FormState {
    snapshot: CancelStatusResponse | null;
    lastFetchedAt: number | null;
    isFetching: boolean;
    isSubmitting: boolean;
    fetchError: FetchError | null;
    toast: ToastState;
    confirmExecuteOpen: boolean;
    confirmFailOpen: boolean;
}

type FormAction =
    | { type: "FETCH_START" }
    | { type: "FETCH_SUCCESS"; snapshot: CancelStatusResponse }
    | { type: "FETCH_ERROR"; message: string; errorReference?: string }
    | { type: "SUBMIT_START" }
    | { type: "SUBMIT_END" }
    | { type: "TOAST"; toast: Omit<ToastState, "open"> }
    | { type: "TOAST_CLOSE" }
    | { type: "TOGGLE_EXECUTE"; open: boolean }
    | { type: "TOGGLE_FAIL"; open: boolean };
//********INTERFACES ENDS*********/

//********REDUCER STARTS*********/
const initialFormState: FormState = {
    snapshot: null,
    lastFetchedAt: null,
    isFetching: false,
    isSubmitting: false,
    fetchError: null,
    toast: { open: false, severity: "info", message: "" },
    confirmExecuteOpen: false,
    confirmFailOpen: false,
};

function formReducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case "FETCH_START":
            return { ...state, isFetching: true, fetchError: null };
        case "FETCH_SUCCESS":
            return {
                ...state,
                isFetching: false,
                snapshot: action.snapshot,
                lastFetchedAt: Date.now(),
                fetchError: null,
            };
        case "FETCH_ERROR":
            return {
                ...state,
                isFetching: false,
                fetchError: { message: action.message, errorReference: action.errorReference },
            };
        case "SUBMIT_START":
            return { ...state, isSubmitting: true };
        case "SUBMIT_END":
            return { ...state, isSubmitting: false };
        case "TOAST":
            return { ...state, toast: { ...action.toast, open: true } };
        case "TOAST_CLOSE":
            return { ...state, toast: { ...state.toast, open: false } };
        case "TOGGLE_EXECUTE":
            return { ...state, confirmExecuteOpen: action.open };
        case "TOGGLE_FAIL":
            return { ...state, confirmFailOpen: action.open };
        default:
            return state;
    }
}
//********REDUCER ENDS*********/

//********ERROR PARSING STARTS*********/
interface ParsedCancelError {
    statusCode: number;
    message: string;
    errorReference?: string;
    validationErrors: string[];
    isStateRace: boolean;
}

// The cancel backend returns { statusCode, error, message, data, errorReference }.
// 409 messages are customer-readable; 422 carries data.validationErrors[]; a
// state-race 409 hints the FE to re-fetch /status.
// The gateway can return an error as an HTTP 200 whose body carries the real
// status (so axios does NOT throw), and may wrap the engine envelope under
// `rawError`. Unwrap to the engine envelope { statusCode, error, message, data,
// errorReference } before reading any field.
function unwrapEnvelope(raw: any): any {
    return raw && typeof raw === "object" && raw.rawError && typeof raw.rawError === "object"
        ? raw.rawError
        : raw;
}

// Decide whether a RESOLVED response body is actually an error. Success envelopes
// use `status: 200`; error envelopes use `statusCode >= 400` or a `rawError`
// wrapper. Returns the raw error body, or null when the response is a success.
function errorEnvelopeOf(res: any): any | null {
    if (!res || typeof res !== "object") return null;
    if (res.rawError) return res;
    const sc = Number(res.statusCode ?? 0);
    return sc >= 400 ? res : null;
}

function parseEnvelope(raw: any): ParsedCancelError {
    const env = unwrapEnvelope(raw) || {};
    const statusCode = Number(env.statusCode ?? raw?.statusCode ?? raw?.status ?? 0) || 0;
    const message = env.message || raw?.message || "Request failed";
    const errorReference = env.errorReference || raw?.errorReference;
    // validationErrors may sit under data.validationErrors OR details.validationErrors
    const validationErrors: string[] = Array.isArray(env?.data?.validationErrors)
        ? env.data.validationErrors
        : Array.isArray(env?.details?.validationErrors)
            ? env.details.validationErrors
            : [];
    // Per contract section 15 the optimistic-lock race hint is the literal phrase
    // "state changed" (+ a retry hint). Do NOT match the bare tokens "state" /
    // "expected" -- those appear in displayable wrong-state 409 messages
    // (e.g. "...not ready for approval (current state: X)") that section 13 says
    // the FE must show the user directly.
    const isStateRace =
        statusCode === 409 && /state changed|please retry|please try again/i.test(message);
    return { statusCode, message, errorReference, validationErrors, isStateRace };
}

// From a THROWN axios error (genuine non-2xx HTTP).
function parseCancelError(err: unknown): ParsedCancelError {
    const e = err as { response?: { status?: number; data?: any; headers?: Record<string, string> }; message?: string };
    const parsed = parseEnvelope(e?.response?.data);
    if (!parsed.statusCode) parsed.statusCode = e?.response?.status ?? 0;
    if (parsed.message === "Request failed") parsed.message = e?.message || parsed.message;
    if (!parsed.errorReference) parsed.errorReference = e?.response?.headers?.["x-trace-id"];
    return parsed;
}

// We surface our OWN copy for errors rather than the raw service message. The
// errorReference (a support code, not a sentence) is still shown for tracing.
const FRIENDLY_ERROR: Record<number, string> = {
    400: "We couldn't process that request. Please refresh and try again.",
    401: "Your session has expired. Please sign in again.",
    403: "You don't have permission to perform this action.",
    404: "This cancellation couldn't be found. Go back and refresh the list.",
    409: "This action isn't available for the booking's current status. Refreshed to the latest state.",
    422: "The cancellation amounts couldn't be validated. Please re-check the fare, taxes, discount, penalty and fees, then resubmit.",
    503: "The service is temporarily unavailable. Please try again in a moment.",
};
const friendlyError = (statusCode: number): string =>
    FRIENDLY_ERROR[statusCode] || "Something went wrong. Please try again.";

//********ERROR PARSING ENDS*********/

//********HELPERS STARTS*********/
const formatMoney = (n: number | string | undefined | null, currency = "USD"): string => {
    const v = typeof n === "string" ? parseFloat(n) : n ?? 0;
    if (Number.isNaN(v)) return `${currency} 0.00`;
    return `${currency} ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatRelative = (ts: number | null): string => {
    if (!ts) return "never";
    const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
};

const formatDateLabel = (iso?: string): string => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
};

const formatExpiresIn = (iso?: string): string => {
    if (!iso) return "-";
    const ms = new Date(iso).getTime() - Date.now();
    if (Number.isNaN(ms)) return "-";
    if (ms <= 0) return "expired";
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remM = minutes % 60;
    return remM > 0 ? `${hours}h ${remM}m` : `${hours}h`;
};

const autoRefreshKey = (txnId: string, status: CancelStatus) =>
    `cancelAutoRefresh:${txnId}:${status}`;

const isManualFlow = (snapshot: CancelStatusResponse): boolean =>
    snapshot.sectors.some((s) => s.quoteSource === "MANUAL") ||
    Boolean(snapshot.quoteDetails?.manualEntry) ||
    Boolean(snapshot.quoteDetails?.manualRescue);

const getAgentId = (): string => {
    const u = getUserDetails();
    return u?.userId || u?.id || u?.email || "OPS_AGENT";
};

const presentPaxTypes = (snapshot: CancelStatusResponse): PaxType[] => {
    const set = new Set<PaxType>();
    (snapshot.passengers || []).forEach((p) => p.paxType && set.add(p.paxType));
    if (set.size === 0) set.add("ADT");
    return Array.from(set);
};
//********HELPERS ENDS*********/

//********PRESENTATIONAL STARTS*********/
const Stepper: React.FC<{ activeStep: number; failed: boolean }> = ({ activeStep, failed }) => (
    <Box sx={{ display: "flex", alignItems: "center", p: "12px 16px", overflowX: "auto" }}>
        {STEPS.map((label, i) => {
            const done = i < activeStep;
            const active = i === activeStep;
            const doneColor = failed ? COLORS.red : COLORS.grn;
            return (
                <React.Fragment key={label}>
                    <Box
                        sx={{
                            width: 22, height: 22, borderRadius: "50%",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 9, fontWeight: 700, flexShrink: 0,
                            color: done || active ? "#fff" : COLORS.dim,
                            background: done ? doneColor : active ? COLORS.ind : "#E5E7EB",
                        }}
                    >
                        {done ? <CheckIcon sx={{ fontSize: 13 }} /> : i + 1}
                    </Box>
                    <Typography
                        sx={{
                            fontSize: 10, ml: "5px", mr: "3px", whiteSpace: "nowrap",
                            fontWeight: active ? 600 : 400,
                            color: active ? COLORS.text : COLORS.dim,
                        }}
                    >
                        {label}
                    </Typography>
                    {i < STEPS.length - 1 && (
                        <Box sx={{ width: 18, height: "1px", flexShrink: 0, background: done ? doneColor : "#E5E7EB" }} />
                    )}
                </React.Fragment>
            );
        })}
    </Box>
);

const PhaseBadge: React.FC<{ phase?: CancelPhase }> = ({ phase }) => {
    if (!phase) return null;
    const palette =
        phase === "GRACE"
            ? { bg: COLORS.redBg, text: COLORS.redDk }
            : phase === "OPS"
            ? { bg: COLORS.ambBg, text: COLORS.ambDk }
            : { bg: COLORS.bluBg, text: COLORS.bluDk };
    return (
        <Chip
            label={PHASE_LABEL[phase]}
            size="small"
            sx={{ height: 20, fontSize: 9, fontWeight: 600, background: palette.bg, color: palette.text }}
        />
    );
};

const BookingDetailCard: React.FC<{
    snapshot: CancelStatusResponse;
    customerName?: string;
    customerEmail?: string;
}> = ({ snapshot, customerName, customerEmail }) => {
    const supplier = snapshot.sectors[0]?.supplierName || "Supplier";
    const isPerSector = snapshot.modifyType === "PER_SECTOR";

    return (
        <Paper elevation={0} sx={{ p: "16px 20px", mb: "12px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "10px" }}>
                <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        Booking: {snapshot.bookingId}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                        {customerName ? `${customerName} - ` : ""}
                        {supplier}
                    </Typography>
                </Box>
                <Chip
                    label={isPerSector ? "Per-sector cancel" : "Full itinerary"}
                    size="small"
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, background: COLORS.redBg, color: COLORS.redDk }}
                />
            </Box>

            {snapshot.sectors.map((sector) => (
                <Box
                    key={sector.transactionId}
                    sx={{
                        display: "flex", alignItems: "center", p: "8px 12px", mb: "5px", borderRadius: 2,
                        background: COLORS.redBg, border: `1px solid ${COLORS.border}`, fontSize: 11,
                    }}
                >
                    <Chip
                        label={sector.quoteSource === "MANUAL" ? "Manual" : "Auto"}
                        size="small"
                        sx={{
                            height: 18, fontSize: 9, fontWeight: 600, mr: "8px",
                            background: sector.quoteSource === "MANUAL" ? COLORS.ambBg : COLORS.bluBg,
                            color: sector.quoteSource === "MANUAL" ? COLORS.ambDk : COLORS.bluDk,
                        }}
                    />
                    <Box sx={{ fontWeight: 600, mr: "10px" }}>
                        {sector.source} -&gt; {sector.destination}
                    </Box>
                    <Box sx={{ color: COLORS.dim }}>
                        {sector.pnr ? `PNR ${sector.pnr} - ` : ""}
                        {formatDateLabel(sector.departureDate)}
                        {sector.cabinClass ? ` - ${sector.cabinClass}` : ""}
                    </Box>
                </Box>
            ))}

            <Box
                sx={{
                    p: "8px 12px", borderRadius: 2, background: COLORS.lgray, border: `1px solid ${COLORS.border}`,
                    fontSize: 11, mt: "6px", display: "flex", gap: "16px", flexWrap: "wrap", color: COLORS.dim,
                }}
            >
                <span>Pax: <b style={{ color: COLORS.text }}>{snapshot.passengerCount ?? snapshot.passengers.length}</b></span>
                {customerEmail && <span>Email: <b style={{ color: COLORS.text }}>{customerEmail}</b></span>}
                {snapshot.booking?.finalPrice != null && (
                    <span>Paid: <b style={{ color: COLORS.text }}>{formatMoney(snapshot.booking.finalPrice)}</b></span>
                )}
                {snapshot.ptrType && (
                    <span>Path: <b style={{ color: COLORS.text }}>{snapshot.ptrType}</b></span>
                )}
            </Box>
        </Paper>
    );
};

const LoadingPanel: React.FC<{ tint: "amber" | "ind"; title: string; subtitle?: string }> = ({
    tint, title, subtitle,
}) => {
    const palette =
        tint === "amber"
            ? { bg: COLORS.ambBg, color: COLORS.amb, border: "rgba(217,119,6,.12)" }
            : { bg: COLORS.indBg, color: COLORS.ind, border: "rgba(79,70,229,.12)" };
    return (
        <Box sx={{ p: "24px", textAlign: "center", borderRadius: 3, background: palette.bg, border: `1px solid ${palette.border}` }}>
            <CircularProgress size={20} sx={{ color: palette.color, mb: "6px" }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: palette.color }}>{title}</Typography>
            {subtitle && <Typography sx={{ fontSize: 12, color: COLORS.dim, mt: "3px" }}>{subtitle}</Typography>}
        </Box>
    );
};

const AutoRefreshPrompt: React.FC<{ cadenceSeconds: number; onAccept: () => void; onDismiss: () => void }> = ({
    cadenceSeconds, onAccept, onDismiss,
}) => (
    <Box
        sx={{
            p: "10px 14px", mb: "12px", borderRadius: 2, background: COLORS.indBg, border: "1px solid rgba(79,70,229,.15)",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px",
        }}
    >
        <Typography sx={{ fontSize: 12, color: COLORS.ind, fontWeight: 600 }}>
            Auto-refresh every {cadenceSeconds}s to see live updates from the supplier?
        </Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
                size="small"
                onClick={onAccept}
                sx={{ background: COLORS.ind, color: "#fff", fontSize: 11, textTransform: "none", fontWeight: 600, "&:hover": { background: "#4338CA" } }}
            >
                Yes, auto-refresh
            </Button>
            <Button size="small" onClick={onDismiss} sx={{ color: COLORS.dim, fontSize: 11, textTransform: "none", fontWeight: 600 }}>
                No, I will refresh manually
            </Button>
        </Box>
    </Box>
);

const ActiveAutoRefreshBar: React.FC<{ cadenceSeconds: number; onPause: () => void }> = ({ cadenceSeconds, onPause }) => (
    <Box
        sx={{
            p: "8px 14px", mb: "12px", borderRadius: 2, background: COLORS.grnBg, border: "1px solid rgba(5,150,105,.15)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
    >
        <Typography sx={{ fontSize: 11, color: COLORS.grn, fontWeight: 600 }}>Auto-refreshing every {cadenceSeconds}s</Typography>
        <Button size="small" onClick={onPause} sx={{ color: COLORS.grn, fontSize: 11, textTransform: "none", fontWeight: 600 }}>
            Pause
        </Button>
    </Box>
);

// Per-sector, per-pax refund breakdown table (read-only) driven by
// quoteDetails.quote.sectorQuotes + bookingTotals.
const RefundBreakdownTable: React.FC<{ snapshot: CancelStatusResponse }> = ({ snapshot }) => {
    const quote = snapshot.quoteDetails?.quote;
    if (!quote) return null;
    const currency = quote.bookingTotals?.currency || "USD";
    const sectorRoute = (txnId: string) => {
        const s = snapshot.sectors.find((x) => x.transactionId === txnId);
        return s ? `${s.source}-${s.destination}` : txnId;
    };

    return (
        <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ "& td, & th": { fontSize: 11, py: "6px" } }}>
                <TableHead>
                    <TableRow sx={{ background: COLORS.lgray }}>
                        <TableCell>Sector</TableCell>
                        <TableCell>Pax</TableCell>
                        <TableCell align="right">Fare</TableCell>
                        <TableCell align="right">Taxes</TableCell>
                        <TableCell align="right">Penalty</TableCell>
                        <TableCell align="right">Fees</TableCell>
                        <TableCell align="right">Refund</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {quote.sectorQuotes.map((sq) =>
                        (Object.keys(sq.paxRates) as PaxType[]).map((pt) => {
                            const r = sq.paxRates[pt] as CancelPaxRate;
                            return (
                                <TableRow key={`${sq.transactionId}-${pt}`}>
                                    <TableCell>{sectorRoute(sq.transactionId)}</TableCell>
                                    <TableCell>
                                        {pt}
                                        {r.seatNumber ? ` · seat ${r.seatNumber}` : ""}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                                        {formatMoney(r.baseAmount, currency)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                                        {formatMoney(r.taxes, currency)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: "monospace", color: COLORS.red }}>
                                        -{formatMoney(r.penalty, currency)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: "monospace", color: COLORS.dim }}>
                                        -{formatMoney(r.markup + r.serviceFee + (r.cancelMarkup ?? 0) + (r.cancelServiceFee ?? 0), currency)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontFamily: "monospace", color: COLORS.grn, fontWeight: 600 }}>
                                        {formatMoney(r.refundAmount, currency)}
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                    {quote.bookingTotals && (
                        <TableRow sx={{ background: COLORS.grnBg }}>
                            <TableCell colSpan={4} sx={{ fontWeight: 700, color: COLORS.grnDk }}>
                                Total refund to customer
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", color: COLORS.red }}>
                                -{formatMoney(quote.bookingTotals.totalPenalty, currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", color: COLORS.dim }}>
                                -{formatMoney(quote.bookingTotals.totalMarkup + quote.bookingTotals.totalServiceFee + (quote.bookingTotals.totalCancelMarkup ?? 0) + (quote.bookingTotals.totalCancelServiceFee ?? 0), currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontFamily: "monospace", color: COLORS.grn, fontWeight: 700, fontSize: 13 }}>
                                {formatMoney(quote.bookingTotals.totalRefund, currency)}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {((quote.bookingTotals?.totalCancelMarkup ?? 0) > 0 ||
                (quote.bookingTotals?.totalCancelServiceFee ?? 0) > 0) && (
                <Typography sx={{ fontSize: 10.5, color: COLORS.dim, mt: "6px" }}>
                    Includes auto-applied cancellation fees (company-retained): cancel markup{" "}
                    {formatMoney(quote.bookingTotals.totalCancelMarkup ?? 0, currency)} &middot; cancel service fee{" "}
                    {formatMoney(quote.bookingTotals.totalCancelServiceFee ?? 0, currency)}.
                </Typography>
            )}
        </Box>
    );
};
//********PRESENTATIONAL ENDS*********/

//********STEP VIEWS STARTS*********/
const StepGenerateQuote: React.FC<{ supplier: string; onGenerate: () => void; submitting: boolean }> = ({
    supplier, onGenerate, submitting,
}) => (
    <Paper elevation={0} sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: "3px" }}>Step 1: Generate cancellation quote</Typography>
        <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "14px" }}>
            Query {supplier} for the void / refund quote. If the supplier has no auto quote API (or it fails), the
            request moves to a manual rescue you complete below.
        </Typography>
        <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <BlockIcon />}
            onClick={onGenerate}
            disabled={submitting}
            sx={{ background: COLORS.red, borderRadius: 2.5, textTransform: "none", fontWeight: 600, "&:hover": { background: COLORS.redDk } }}
        >
            Generate quote
        </Button>
    </Paper>
);

// String-backed so inputs can be empty (no forced "0") and accept decimals like
// "0.5" mid-type. Parsed to numbers only at submit.
interface ManualRow {
    baseAmount: string;
    taxes: string;
    markup: string;
    serviceFee: string;
    discount: string;
    penalty: string;
}
const EMPTY_MANUAL_ROW: ManualRow = {
    baseAmount: "", taxes: "", markup: "", serviceFee: "", discount: "", penalty: "",
};
const numOf = (s: string): number => {
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
};
const numStr = (v: number | undefined | null): string =>
    v != null && Number.isFinite(v) ? String(v) : "";

// Pre-fill a manual-rescue row from the booking snapshot: base/taxes/markup/
// serviceFee come from the matching passenger's stored fares, discount from the
// sector's apportioned discount (split per pax on the sector). Penalty is left
// empty -- that is the supplier-provided cancellation charge ops enters.
function prefillManualRow(snapshot: CancelStatusResponse, sector: CancelSector, pt: PaxType): ManualRow {
    const paxOnSector = (snapshot.passengers || []).filter((p) => p.transactionId === sector.transactionId);
    const sample = paxOnSector.find((p) => p.paxType === pt);
    const f = sample?.fares;
    const paxCount = paxOnSector.length || 1;
    const perPaxDiscount = (sector.apportionedDiscount ?? 0) / paxCount;
    return {
        baseAmount: numStr(f?.baseFare ?? sector.baseFare),
        taxes: numStr(f?.taxes ?? sector.taxes),
        markup: numStr(f?.markup),
        serviceFee: numStr(f?.serviceFee),
        discount: perPaxDiscount ? numStr(Math.round(perPaxDiscount * 100) / 100) : "",
        penalty: "",
    };
}
interface StepManualQuoteProps {
    snapshot: CancelStatusResponse;
    submitting: boolean;
    onSubmit: (ptrType: PtrType, rows: Record<string, Partial<Record<PaxType, ManualRow>>>) => void;
}
const StepManualQuote: React.FC<StepManualQuoteProps> = ({ snapshot, submitting, onSubmit }) => {
    const paxTypes = presentPaxTypes(snapshot);
    const [ptrType, setPtrType] = useState<PtrType>(snapshot.ptrType || "Refund");
    const [rows, setRows] = useState<Record<string, Partial<Record<PaxType, ManualRow>>>>(() => {
        const init: Record<string, Partial<Record<PaxType, ManualRow>>> = {};
        snapshot.sectors.forEach((s) => {
            init[s.transactionId] = {};
            paxTypes.forEach((pt) => {
                init[s.transactionId][pt] = prefillManualRow(snapshot, s, pt);
            });
        });
        return init;
    });

    const update = (txnId: string, pt: PaxType, field: keyof ManualRow, value: string) => {
        setRows((prev) => ({
            ...prev,
            [txnId]: { ...prev[txnId], [pt]: { ...(prev[txnId]?.[pt] as ManualRow), [field]: value } },
        }));
    };

    // Backend-confirmed (cancellation-flow Section 7 / index.js:346): totalAmount is
    // the GROSS paid (base + taxes + markup + serviceFee). Refund subtracts the
    // retained margin (markup + serviceFee) and supplier charges, netting to
    // base + taxes - discount - penalty. Must match the AUTO normalizer.
    const totalFor = (r: ManualRow) =>
        numOf(r.baseAmount) + numOf(r.taxes) + numOf(r.markup) + numOf(r.serviceFee);
    const refundFor = (r: ManualRow) =>
        totalFor(r) - numOf(r.discount) - numOf(r.serviceFee) - numOf(r.markup) - numOf(r.penalty);

    return (
        <Paper elevation={0} sx={{ p: "16px 20px", border: `1px dashed ${COLORS.amb}`, borderRadius: 3, background: COLORS.ambBg }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.ambDk, mb: "3px" }}>
                Manual rescue: enter supplier quote
            </Typography>
            <Typography sx={{ fontSize: 11, color: COLORS.dim, mb: "12px" }}>
                {snapshot.quoteDetails?.rescueReason
                    ? `Reason: ${snapshot.quoteDetails.rescueReason}. `
                    : ""}
                Fare details are pre-filled from the booking. Enter the supplier's cancellation penalty (per sector,
                per pax). The figures below are an estimate; the backend computes the final refund on submit and may
                apply auto cancellation fees (markup and service fee are retained margin, not refunded).
            </Typography>

            <Box sx={{ mb: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                <Typography sx={{ fontSize: 11, color: COLORS.dim }}>Cancellation path</Typography>
                <Select size="small" value={ptrType} onChange={(e) => setPtrType(e.target.value as PtrType)} sx={{ minWidth: 120, background: COLORS.card }}>
                    <MenuItem value="Void">Void (full refund)</MenuItem>
                    <MenuItem value="Refund">Refund (penalty)</MenuItem>
                </Select>
            </Box>

            {snapshot.sectors.map((sector) => (
                <Box key={sector.transactionId} sx={{ p: "10px 12px", mb: "8px", background: COLORS.card, borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, mb: "6px" }}>
                        {sector.source} -&gt; {sector.destination}
                    </Typography>
                    {paxTypes.map((pt) => {
                        const r = (rows[sector.transactionId]?.[pt] as ManualRow) || EMPTY_MANUAL_ROW;
                        return (
                            <Box key={pt} sx={{ display: "grid", gridTemplateColumns: "44px repeat(6, 1fr) auto", gap: "6px", alignItems: "center", mb: "6px" }}>
                                <Typography sx={{ fontSize: 11, color: COLORS.dim, fontWeight: 600 }}>{pt}</Typography>
                                {(["baseAmount", "taxes", "markup", "discount", "penalty", "serviceFee"] as const).map((field) => (
                                    <TextField
                                        key={field}
                                        size="small"
                                        type="number"
                                        placeholder="0.00"
                                        label={field === "baseAmount" ? "base" : field === "serviceFee" ? "svc fee" : field}
                                        value={r[field]}
                                        onChange={(e) => update(sector.transactionId, pt, field, e.target.value)}
                                        InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                        sx={{ "& input": { fontSize: 12 } }}
                                    />
                                ))}
                                <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                    <Typography sx={{ fontSize: 10, color: COLORS.dim, fontFamily: "monospace" }}>
                                        Est. total {formatMoney(totalFor(r))}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: "monospace", color: COLORS.grn }}>
                                        Est. refund {formatMoney(refundFor(r))}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ))}

            <Button
                fullWidth
                variant="contained"
                startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SendIcon />}
                onClick={() => onSubmit(ptrType, rows)}
                disabled={submitting}
                sx={{ background: COLORS.red, borderRadius: 2.5, textTransform: "none", fontWeight: 600, py: "10px", "&:hover": { background: COLORS.redDk } }}
            >
                Submit quote
            </Button>
        </Paper>
    );
};

const StepReviewQuote: React.FC<{ snapshot: CancelStatusResponse; onSend: () => void; submitting: boolean }> = ({
    snapshot, onSend, submitting,
}) => {
    const ptr = normalizePtrType(snapshot.ptrType);
    return (
        <Paper elevation={0} sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "3px" }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Cancellation refund quote</Typography>
                {ptr && (
                    <Chip
                        label={ptr === "Void" ? "Void - full refund" : "Refund - charges apply"}
                        size="small"
                        sx={{ height: 22, fontSize: 10, fontWeight: 600, background: ptr === "Void" ? COLORS.grnBg : COLORS.ambBg, color: ptr === "Void" ? COLORS.grnDk : COLORS.ambDk }}
                    />
                )}
            </Box>
            <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "12px" }}>
                Per-sector, per-passenger penalty and refund breakdown. Review then send to the customer.
            </Typography>

            <RefundBreakdownTable snapshot={snapshot} />

            <Button
                fullWidth
                variant="contained"
                startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SendIcon />}
                onClick={onSend}
                disabled={submitting}
                sx={{ mt: "14px", background: COLORS.red, borderRadius: 2.5, textTransform: "none", fontWeight: 600, py: "10px", "&:hover": { background: COLORS.redDk } }}
            >
                Send cancel quote to customer
            </Button>
        </Paper>
    );
};

const StepSentToCustomer: React.FC<{ snapshot: CancelStatusResponse }> = ({ snapshot }) => {
    const totals = snapshot.quoteDetails?.quote?.bookingTotals;
    const currency = totals?.currency || "USD";
    return (
        <Paper elevation={0} sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", background: COLORS.grnBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckIcon sx={{ fontSize: 16, color: COLORS.grn }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Cancel quote sent to customer</Typography>
                    <Typography sx={{ fontSize: 11, color: COLORS.dim }}>Awaiting response in MyTrip</Typography>
                </Box>
            </Box>
            <Box sx={{ p: "10px 14px", borderRadius: 2, background: COLORS.ambBg, border: "1px solid rgba(217,119,6,.12)", fontSize: 11, color: COLORS.ambDk, fontWeight: 600, mb: "12px" }}>
                Quote expires in {formatExpiresIn(snapshot.expiresAt)} ({formatDateLabel(snapshot.expiresAt)}). Auto-expires if the customer does not respond.
            </Box>
            {snapshot.ptrType === "Void" && snapshot.voidingWindowExpiresAt && (
                <Box sx={{ p: "10px 14px", borderRadius: 2, background: COLORS.redBg, border: "1px solid rgba(220,38,38,.12)", fontSize: 11, color: COLORS.redDk, fontWeight: 600, mb: "12px" }}>
                    Void window closes {formatDateLabel(snapshot.voidingWindowExpiresAt)} - after this the full-refund Void path lapses and the customer review window is clamped to it.
                </Box>
            )}
            {totals && (
                <Box sx={{ p: "12px 14px", borderRadius: 2, background: COLORS.lgray, border: `1px solid ${COLORS.border}` }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, p: "3px 0" }}>
                        <span>Net refund if approved</span>
                        <span style={{ fontFamily: "monospace", fontWeight: 700, color: COLORS.grn }}>{formatMoney(totals.totalRefund, currency)}</span>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, p: "3px 0" }}>
                        <span style={{ color: COLORS.dim }}>Cancellation penalty</span>
                        <span style={{ fontFamily: "monospace", color: COLORS.red }}>{formatMoney(totals.totalPenalty, currency)}</span>
                    </Box>
                </Box>
            )}
            <Typography sx={{ fontSize: 11, color: COLORS.dim, mt: "12px" }}>
                The customer approves or declines from the MyTrip app. Refresh to see updates.
            </Typography>
        </Paper>
    );
};

const StepExecute: React.FC<{ snapshot: CancelStatusResponse; onExecute: () => void; submitting: boolean }> = ({
    snapshot, onExecute, submitting,
}) => (
    <Paper elevation={0} sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "12px" }}>
            <Box sx={{ width: 28, height: 28, borderRadius: "50%", background: COLORS.grnBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CheckIcon sx={{ fontSize: 16, color: COLORS.grn }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Customer approved cancellation</Typography>
                <Typography sx={{ fontSize: 11, color: COLORS.dim }}>Execute the supplier void / refund now.</Typography>
            </Box>
        </Box>
        <Box sx={{ p: "10px 14px", borderRadius: 2, background: COLORS.bluBg, border: "1px solid rgba(37,99,235,.12)", fontSize: 12, color: COLORS.bluDk, mb: "12px" }}>
            Unlike modify, cancellation goes directly to supplier execution after approval - no payment step.
            Execute window expires in {formatExpiresIn(snapshot.expiresAt)}.
        </Box>
        <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <BlockIcon />}
            onClick={onExecute}
            disabled={submitting}
            sx={{ background: COLORS.ind, borderRadius: 2.5, textTransform: "none", fontWeight: 600, "&:hover": { background: "#4338CA" } }}
        >
            Execute cancellation with supplier
        </Button>
    </Paper>
);

interface MarkRefundedFormProps {
    snapshot: CancelStatusResponse;
    submitting: boolean;
    heading: string;
    note: string;
    onMarkRefunded: (refundMethod: string, refundReference: string) => void;
    onOpenFail: () => void;
}
const MarkRefundedForm: React.FC<MarkRefundedFormProps> = ({
    snapshot, submitting, heading, note, onMarkRefunded, onOpenFail,
}) => {
    const [refundMethod, setRefundMethod] = useState("");
    const [refundReference, setRefundReference] = useState("");
    const totals = snapshot.quoteDetails?.quote?.bookingTotals;
    const currency = totals?.currency || "USD";

    return (
        <Paper elevation={0} sx={{ p: "16px 20px", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: "3px" }}>{heading}</Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.dim, mb: "12px" }}>{note}</Typography>

            {totals && (
                <Box sx={{ p: "10px 14px", mb: "12px", borderRadius: 2, background: COLORS.lgray, border: `1px solid ${COLORS.border}` }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, p: "3px 0" }}>
                        <span style={{ color: COLORS.dim }}>Cancellation penalty</span>
                        <span style={{ fontFamily: "monospace", color: COLORS.red }}>-{formatMoney(totals.totalPenalty, currency)}</span>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13, p: "6px 0 0", borderTop: `1px solid ${COLORS.border}`, mt: "4px", fontWeight: 700 }}>
                        <span style={{ color: COLORS.grnDk }}>Refund amount</span>
                        <span style={{ fontFamily: "monospace", color: COLORS.grn }}>{formatMoney(totals.totalRefund, currency)}</span>
                    </Box>
                </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", mb: "12px" }}>
                <TextField size="small" label="Refund method" placeholder="ORIGINAL_PAYMENT" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)} />
                <TextField size="small" label="Refund reference" placeholder="stripe_re_..." value={refundReference} onChange={(e) => setRefundReference(e.target.value)} />
            </Box>

            <Button
                fullWidth
                variant="contained"
                disabled={submitting}
                onClick={() => onMarkRefunded(refundMethod, refundReference)}
                sx={{ background: COLORS.grn, textTransform: "none", fontWeight: 600, borderRadius: 2.5, py: "10px", "&:hover": { background: "#047857" } }}
            >
                Mark refund processed
            </Button>
            <Box sx={{ textAlign: "center", mt: "8px" }}>
                <Button onClick={onOpenFail} disabled={submitting} sx={{ textTransform: "none", fontSize: 11, color: COLORS.red }}>
                    Supplier refused - mark cancellation failed
                </Button>
            </Box>
        </Paper>
    );
};

const StepResult: React.FC<{ snapshot: CancelStatusResponse }> = ({ snapshot }) => {
    const success = snapshot.status === "CANCELLED";
    const totals = snapshot.quoteDetails?.quote?.bookingTotals;
    const currency = totals?.currency || "USD";
    const title =
        snapshot.status === "CANCELLED"
            ? "Cancellation complete"
            : snapshot.status === "CANCEL_FAILED"
            ? "Cancellation failed"
            : snapshot.status === "QUOTE_EXPIRED"
            ? "Quote expired"
            : snapshot.status === "AUTO_CANCELLED"
            ? "Cancel request timed out"
            : "Cancellation failed";
    const sub =
        snapshot.status === "CANCELLED"
            ? `Tickets voided. Refund of ${formatMoney(snapshot.customerTotal ?? totals?.totalRefund, currency)} initiated to the original payment method (5-7 business days).`
            : snapshot.status === "CANCEL_FAILED"
            ? `${snapshot.cancellationReason || "The airline could not process the cancellation."} The original booking remains valid.`
            : snapshot.status === "QUOTE_EXPIRED"
            ? "The customer did not respond within the review window. The booking remains active."
            : snapshot.status === "AUTO_CANCELLED"
            ? "The cancel request timed out without resolution. The booking remains active."
            : `${snapshot.cancellationReason || "The cancellation could not be completed."}`;

    return (
        <Paper elevation={0} sx={{ p: "32px 24px", textAlign: "center", border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: "50%", mx: "auto", mb: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: success ? COLORS.grn : COLORS.red }}>
                {success ? <CheckIcon sx={{ color: "#fff", fontSize: 26 }} /> : <CloseIcon sx={{ color: "#fff", fontSize: 26 }} />}
            </Box>
            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: "6px", color: success ? COLORS.grnDk : COLORS.redDk }}>{title}</Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.dim, maxWidth: 440, mx: "auto" }}>{sub}</Typography>
        </Paper>
    );
};
//********MAIN COMPONENT STARTS*********/
const CancelBookingWorkflow: React.FC<CancelBookingWorkflowProps> = ({
    cancelTransactionId,
    itineraryId,
    customerName,
    customerEmail,
    onBack,
}) => {
    const [state, dispatch] = useReducer(formReducer, initialFormState);
    const [autoRefreshDecided, setAutoRefreshDecided] = useState<"yes" | "no" | null>(null);
    const [autoRefreshActive, setAutoRefreshActive] = useState(false);
    const [showAutoRefreshPrompt, setShowAutoRefreshPrompt] = useState(false);
    const [failReason, setFailReason] = useState("");
    const pollTimerRef = useRef<number | null>(null);
    const promptTimerRef = useRef<number | null>(null);
    //********INITIATE STATE ENDS*********/

    const snapshot = state.snapshot;
    const status: CancelStatus | null = snapshot ? snapshot.status : null;
    // Auto-refresh only for supplier-driven waits. A MANUAL-rescue QUOTE_PENDING
    // shows the ops entry form (no supplier polling) -- suppress the prompt there.
    const cadence: number | undefined =
        status && !(status === "QUOTE_PENDING" && snapshot && isManualFlow(snapshot))
            ? POLL_CADENCE_SECONDS[status]
            : undefined;
    const isTerminal = status ? isTerminalCancelStatus(status) : false;

    //********HANDLERS STARTS*********/
    const refresh = useCallback(async () => {
        dispatch({ type: "FETCH_START" });
        try {
            const res = await getCancelStatus(cancelTransactionId);
            const errEnv = errorEnvelopeOf(res);
            if (errEnv) {
                const parsed = parseEnvelope(errEnv);
                const msg = friendlyError(parsed.statusCode);
                dispatch({ type: "FETCH_ERROR", message: msg, errorReference: parsed.errorReference });
                dispatch({ type: "TOAST", toast: { severity: "error", message: msg, errorReference: parsed.errorReference } });
                return;
            }
            dispatch({ type: "FETCH_SUCCESS", snapshot: res.data });
        } catch (err) {
            const parsed = parseCancelError(err);
            const msg = friendlyError(parsed.statusCode);
            dispatch({ type: "FETCH_ERROR", message: msg, errorReference: parsed.errorReference });
            dispatch({ type: "TOAST", toast: { severity: "error", message: msg, errorReference: parsed.errorReference } });
        }
    }, [cancelTransactionId]);

    const submitAction = useCallback(
        async (fn: () => Promise<any>, successMessage: string) => {
            dispatch({ type: "SUBMIT_START" });
            // Shared error handler for both resolved-error bodies and thrown errors.
            // Always our own copy (never the raw service message). Any 409 means the
            // row was not in the expected state -> re-fetch and show the truth.
            const showError = async (parsed: ParsedCancelError) => {
                if (parsed.statusCode === 409) {
                    dispatch({ type: "TOAST", toast: { severity: "info", message: friendlyError(409), errorReference: parsed.errorReference } });
                    await refresh();
                } else {
                    // 422 carries field-level validationErrors (e.g. the exact expected
                    // refund per sector/pax). This is an ops tool -- surface those
                    // specifics so the agent can correct the manual entry, instead of
                    // only the generic re-check prompt (contract section 13).
                    const detail =
                        parsed.statusCode === 422 && parsed.validationErrors.length
                            ? ` ${parsed.validationErrors.join("; ")}`
                            : "";
                    dispatch({
                        type: "TOAST",
                        toast: { severity: "error", message: `${friendlyError(parsed.statusCode)}${detail}`, errorReference: parsed.errorReference },
                    });
                }
            };
            try {
                const res = await fn();
                // The gateway returns some errors as HTTP 200 with the real status in
                // the body, so a resolved response can still be a failure -- check it
                // before declaring success (otherwise a 409 shows a green toast).
                const errEnv = errorEnvelopeOf(res);
                if (errEnv) {
                    await showError(parseEnvelope(errEnv));
                    return;
                }
                // Best-effort customer email (contract 3.4 / 3.8): state advances but
                // SES delivery may have failed -- warn rather than claim "sent".
                const d = res?.data || {};
                const emailFailed = d.emailSent === false || d.customerEmailSent === false;
                if (emailFailed) {
                    const reason = d.emailError || d.customerEmailError || "email delivery failed";
                    dispatch({
                        type: "TOAST",
                        toast: { severity: "warning", message: `${successMessage}, but the customer was NOT emailed: ${reason}` },
                    });
                } else {
                    dispatch({ type: "TOAST", toast: { severity: "success", message: successMessage } });
                }
                await refresh();
            } catch (err) {
                await showError(parseCancelError(err));
            } finally {
                dispatch({ type: "SUBMIT_END" });
            }
        },
        [refresh]
    );

    const handleGenerateQuote = () =>
        submitAction(() => generateQuote({ cancelTransactionId }), "Quote generation started");

    const handleSubmitManualQuote = (
        ptrType: PtrType,
        rows: Record<string, Partial<Record<PaxType, ManualRow>>>
    ) => {
        const currency = snapshot?.quoteDetails?.quote?.bookingTotals?.currency || "USD";
        // Send ONLY the six inputs per pax (SMB-3206). The backend computes
        // totalAmount, refundAmount, and the cancellation fees / seat charges and
        // returns them; we must NOT send (or re-derive) totalAmount / refundAmount.
        const sectorQuotes = Object.entries(rows).map(([transactionId, byPax]) => {
            const paxRates: Partial<Record<PaxType, ManualQuoteInputRate>> = {};
            (Object.keys(byPax) as PaxType[]).forEach((pt) => {
                const r = byPax[pt] as ManualRow;
                paxRates[pt] = {
                    baseAmount: numOf(r.baseAmount),
                    taxes: numOf(r.taxes),
                    markup: numOf(r.markup),
                    serviceFee: numOf(r.serviceFee),
                    discount: numOf(r.discount),
                    penalty: numOf(r.penalty),
                };
            });
            return { transactionId, paxRates };
        });
        return submitAction(
            () => submitManualQuote({ cancelTransactionId, ptrType, currency, sectorQuotes }),
            "Manual quote submitted and sent to customer"
        );
    };

    const handleSendQuote = () =>
        submitAction(() => sendQuote({ cancelTransactionId, agentId: getAgentId() }), "Quote sent to customer");

    const handleExecute = () => {
        dispatch({ type: "TOGGLE_EXECUTE", open: false });
        submitAction(() => executeCancel({ cancelTransactionId, agentId: getAgentId() }), "Cancellation dispatched to supplier");
    };

    const handleMarkRefunded = (refundMethod: string, refundReference: string) =>
        submitAction(
            () =>
                markRefunded({
                    cancelTransactionId,
                    agentId: getAgentId(),
                    refund_method: refundMethod || undefined,
                    refund_reference: refundReference || undefined,
                }),
            "Refund marked complete"
        );

    const handleMarkFailed = () => {
        const reason = failReason.trim();
        if (!reason) return;
        dispatch({ type: "TOGGLE_FAIL", open: false });
        setFailReason("");
        submitAction(
            () => markCancelFailed({ cancelTransactionId, failureReason: reason, agentId: getAgentId() }),
            "Marked cancellation failed; customer notified"
        );
    };

    //********HANDLERS ENDS*********/

    //********EFFECTS STARTS*********/
    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancelTransactionId]);

    // Read the saved auto-refresh decision whenever the status changes.
    useEffect(() => {
        if (!status) return;
        const stored = sessionStorage.getItem(autoRefreshKey(cancelTransactionId, status));
        setAutoRefreshDecided(stored === "yes" || stored === "no" ? stored : null);
        setAutoRefreshActive(stored === "yes" && Boolean(cadence));
        setShowAutoRefreshPrompt(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, cancelTransactionId]);

    // After a delay on a poll-eligible state, surface the opt-in prompt.
    useEffect(() => {
        if (promptTimerRef.current) {
            window.clearTimeout(promptTimerRef.current);
            promptTimerRef.current = null;
        }
        if (!cadence || autoRefreshDecided !== null || isTerminal) return;
        promptTimerRef.current = window.setTimeout(() => setShowAutoRefreshPrompt(true), PROMPT_DELAY_MS);
        return () => {
            if (promptTimerRef.current) {
                window.clearTimeout(promptTimerRef.current);
                promptTimerRef.current = null;
            }
        };
    }, [cadence, autoRefreshDecided, isTerminal, status]);

    // Run the polling interval only when ops opts in.
    useEffect(() => {
        if (pollTimerRef.current) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (!autoRefreshActive || !cadence) return;
        pollTimerRef.current = window.setInterval(() => refresh(), cadence * 1000);
        return () => {
            if (pollTimerRef.current) {
                window.clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [autoRefreshActive, cadence, refresh]);

    // Focus refetch for the customer-action state.
    useEffect(() => {
        if (status !== "SENT_TO_CUSTOMER") return;
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [status, refresh]);
    //********EFFECTS ENDS*********/

    //********AUTO-REFRESH HANDLERS STARTS*********/
    const acceptAutoRefresh = () => {
        if (!status) return;
        sessionStorage.setItem(autoRefreshKey(cancelTransactionId, status), "yes");
        setAutoRefreshDecided("yes");
        setAutoRefreshActive(true);
        setShowAutoRefreshPrompt(false);
    };
    const dismissAutoRefresh = () => {
        if (!status) return;
        sessionStorage.setItem(autoRefreshKey(cancelTransactionId, status), "no");
        setAutoRefreshDecided("no");
        setAutoRefreshActive(false);
        setShowAutoRefreshPrompt(false);
    };
    const pauseAutoRefresh = () => {
        if (!status) return;
        sessionStorage.removeItem(autoRefreshKey(cancelTransactionId, status));
        setAutoRefreshDecided(null);
        setAutoRefreshActive(false);
    };
    //********AUTO-REFRESH HANDLERS ENDS*********/

    //********RENDER STARTS*********/
    if (!snapshot && state.isFetching) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!snapshot && state.fetchError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {state.fetchError.message}
                    {state.fetchError.errorReference && (
                        <Typography sx={{ fontSize: 10, mt: 1, fontFamily: "monospace" }}>
                            ref: {state.fetchError.errorReference}
                        </Typography>
                    )}
                </Alert>
                <Box sx={{ display: "flex", gap: "8px" }}>
                    <Button
                        variant="contained"
                        startIcon={state.isFetching ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <RefreshIcon />}
                        onClick={refresh}
                        disabled={state.isFetching}
                        sx={{ background: COLORS.ind, textTransform: "none", "&:hover": { background: "#4338CA" } }}
                    >
                        Retry
                    </Button>
                    <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ textTransform: "none", color: COLORS.dim }}>
                        Back to transactions
                    </Button>
                </Box>
            </Box>
        );
    }

    if (!snapshot) return null;

    const stepIndex = STATE_TO_STEP[snapshot.status];
    const chipPalette = STATE_CHIP_COLOR[snapshot.status];
    const supplierName = snapshot.sectors[0]?.supplierName || "supplier";
    const manualFlow = isManualFlow(snapshot);

    let body: React.ReactNode = null;
    if (snapshot.status === "CANCEL_REQUESTED") {
        body = <StepGenerateQuote supplier={supplierName} onGenerate={handleGenerateQuote} submitting={state.isSubmitting} />;
    } else if (snapshot.status === "QUOTE_PENDING") {
        // MANUAL rows (MANUAL-only suppliers, or AUTO rows that failed and were
        // flipped to MANUAL) expose the manual entry form. AUTO rows still being
        // polled have quoteSource 'AUTO' and show the polling panel instead.
        // Note: /status does not surface manualRescue/phase, so quoteSource is the
        // signal; if the backend isn't ready, the submit's 409 is handled gracefully.
        if (manualFlow) {
            body = <StepManualQuote snapshot={snapshot} submitting={state.isSubmitting} onSubmit={handleSubmitManualQuote} />;
        } else {
            body = (
                <LoadingPanel
                    tint="amber"
                    title={`Waiting on ${supplierName} quote...`}
                    subtitle="Supplier is being polled. If it cannot quote automatically this moves to a manual rescue. Use Refresh or auto-refresh."
                />
            );
        }
    } else if (snapshot.status === "QUOTE_READY") {
        body = <StepReviewQuote snapshot={snapshot} onSend={handleSendQuote} submitting={state.isSubmitting} />;
    } else if (snapshot.status === "SENT_TO_CUSTOMER") {
        body = <StepSentToCustomer snapshot={snapshot} />;
    } else if (snapshot.status === "CUSTOMER_APPROVED") {
        if (manualFlow) {
            body = (
                <MarkRefundedForm
                    snapshot={snapshot}
                    submitting={state.isSubmitting}
                    heading="Process the refund offline"
                    note="MANUAL flow: contact the supplier to void / refund, process the refund to the customer, then mark it here."
                    onMarkRefunded={handleMarkRefunded}
                    onOpenFail={() => dispatch({ type: "TOGGLE_FAIL", open: true })}
                />
            );
        } else {
            body = <StepExecute snapshot={snapshot} onExecute={() => dispatch({ type: "TOGGLE_EXECUTE", open: true })} submitting={state.isSubmitting} />;
        }
    } else if (snapshot.status === "CANCELLING") {
        body = (
            <LoadingPanel
                tint="amber"
                title={`Cancelling with ${supplierName}...`}
                subtitle="Supplier is voiding tickets. Updates arrive via auto-refresh or Refresh."
            />
        );
    } else if (snapshot.status === "SUPPLIER_CONFIRMED") {
        body = <LoadingPanel tint="ind" title="Supplier confirmed - finalizing refund..." subtitle="Booking is being flipped to cancelled." />;
    } else if (snapshot.status === "REFUND_PENDING") {
        body = (
            <MarkRefundedForm
                snapshot={snapshot}
                submitting={state.isSubmitting}
                heading="Refund pending"
                note="Supplier confirmed the cancellation. Accounts processes the refund, then marks it complete here."
                onMarkRefunded={handleMarkRefunded}
                onOpenFail={() => dispatch({ type: "TOGGLE_FAIL", open: true })}
            />
        );
    } else {
        // terminal states: CANCELLED / CANCEL_FAILED / QUOTE_EXPIRED / AUTO_CANCELLED / FAILED
        body = <StepResult snapshot={snapshot} />;
    }

    // ops can fail a stuck row from these states (mark-refunded FAILED)
    const canMarkFailed =
        snapshot.status === "QUOTE_PENDING" ||
        snapshot.status === "CUSTOMER_APPROVED" ||
        snapshot.status === "CANCELLING";

    return (
        <Box sx={{ background: COLORS.bg, minHeight: "100vh", py: "20px" }}>
            <Box sx={{ maxWidth: 900, mx: "auto", px: "16px" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "14px" }}>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{
                            textTransform: "none", borderRadius: 2, borderColor: COLORS.border, color: COLORS.dim,
                            fontSize: 12, fontWeight: 500, background: COLORS.card,
                            "&:hover": { background: COLORS.lgray, borderColor: COLORS.border },
                        }}
                    >
                        Back to transactions
                    </Button>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <Typography sx={{ fontSize: 11, color: COLORS.dim }}>
                            Last updated {formatRelative(state.lastFetchedAt)}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={state.isFetching ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 14 }} />}
                            onClick={refresh}
                            disabled={state.isFetching}
                            sx={{ textTransform: "none", borderRadius: 2, fontSize: 11, borderColor: COLORS.border, color: COLORS.dim }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: "14px" }}>
                    <Box>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: COLORS.red, textTransform: "uppercase", letterSpacing: "1.5px", mb: "2px" }}>
                            Cancel booking workflow
                        </Typography>
                        <Typography component="h1" sx={{ fontSize: 18, fontWeight: 700 }}>
                            {customerName || snapshot.bookingId} - {snapshot.bookingId}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <PhaseBadge phase={snapshot.phase || snapshot.quoteDetails?.phase} />
                        <Chip
                            label={STATE_LABEL[snapshot.status]}
                            sx={{ background: chipPalette.bg, color: chipPalette.text, fontWeight: 600, fontSize: 10, height: 22, borderRadius: 2 }}
                        />
                    </Box>
                </Box>

                <Paper elevation={0} sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 3, mb: "14px" }}>
                    <Stepper activeStep={stepIndex} failed={snapshot.status === "CANCEL_FAILED" || snapshot.status === "FAILED" || snapshot.status === "AUTO_CANCELLED" || snapshot.status === "QUOTE_EXPIRED"} />
                </Paper>

                {showAutoRefreshPrompt && cadence && (
                    <AutoRefreshPrompt cadenceSeconds={cadence} onAccept={acceptAutoRefresh} onDismiss={dismissAutoRefresh} />
                )}
                {autoRefreshActive && cadence && <ActiveAutoRefreshBar cadenceSeconds={cadence} onPause={pauseAutoRefresh} />}

                <BookingDetailCard snapshot={snapshot} customerName={customerName} customerEmail={customerEmail} />

                {body}

                {/* Footer fail button: shown when the body itself doesn't already carry
                    a fail action. MANUAL CUSTOMER_APPROVED uses MarkRefundedForm (which has
                    its own "mark failed"); AUTO CUSTOMER_APPROVED uses StepExecute (no fail),
                    so it still needs the footer escape (doc allows FAILED from CUSTOMER_APPROVED). */}
                {!isTerminal && canMarkFailed && !(snapshot.status === "CUSTOMER_APPROVED" && manualFlow) && (
                    <Box sx={{ mt: "12px", textAlign: "right" }}>
                        <Button
                            size="small"
                            onClick={() => dispatch({ type: "TOGGLE_FAIL", open: true })}
                            disabled={state.isSubmitting}
                            sx={{ textTransform: "none", fontSize: 12, color: COLORS.red }}
                        >
                            Mark cancellation failed
                        </Button>
                    </Box>
                )}
            </Box>

            <Dialog open={state.confirmExecuteOpen} onClose={() => dispatch({ type: "TOGGLE_EXECUTE", open: false })}>
                <DialogTitle>Execute cancellation?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 13 }}>
                        This dispatches the void / refund to {supplierName}. Tickets will be voided. This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch({ type: "TOGGLE_EXECUTE", open: false })}>Keep waiting</Button>
                    <Button onClick={handleExecute} sx={{ color: COLORS.red }}>
                        Yes, execute
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={state.confirmFailOpen}
                onClose={() => {
                    dispatch({ type: "TOGGLE_FAIL", open: false });
                    setFailReason("");
                }}
            >
                <DialogTitle>Mark cancellation failed</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 13, mb: 2 }}>
                        Use this when the supplier refused the cancellation. The booking reverts to valid and the customer
                        is notified. A failure reason is required.
                    </DialogContentText>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        label="Failure reason"
                        value={failReason}
                        onChange={(e) => setFailReason(e.target.value)}
                        placeholder="Mystifly returned 'PNR already used for another transaction' - cannot void"
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            dispatch({ type: "TOGGLE_FAIL", open: false });
                            setFailReason("");
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleMarkFailed} disabled={!failReason.trim()} sx={{ color: COLORS.red }}>
                        Mark failed
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={state.toast.open}
                autoHideDuration={6000}
                onClose={() => dispatch({ type: "TOAST_CLOSE" })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={state.toast.severity}
                    onClose={() => dispatch({ type: "TOAST_CLOSE" })}
                    onClick={() => {
                        if (state.toast.errorReference && navigator.clipboard) {
                            navigator.clipboard.writeText(state.toast.errorReference);
                        }
                    }}
                    sx={{ cursor: state.toast.errorReference ? "pointer" : "default" }}
                >
                    {state.toast.message}
                    {state.toast.errorReference && (
                        <Typography sx={{ fontSize: 10, mt: 0.5, fontFamily: "monospace", opacity: 0.8 }}>
                            ref: {state.toast.errorReference} (click to copy)
                        </Typography>
                    )}
                </Alert>
            </Snackbar>
        </Box>
    );
    //********RENDER ENDS*********/
};
//********MAIN COMPONENT ENDS*********/

export default CancelBookingWorkflow;
